/**
 * Edit Acquisition Type: how the membership was acquired, and nothing else.
 *
 * Ported from legacy's acquisition modal, and the same sheet as
 * `CardInfoDialog` — one field on the left half, Cancel and Submit sharing the
 * footer. Written out rather than drawn from field metadata for the same
 * reason the card is: a dialog holding one control does not want the full
 * width of a form under it.
 *
 * Only a member is enrolled over the counter or by a canvasser; the other
 * three tiers come in as a benefit, which is not a choice, so the detail page
 * offers this on a member and nowhere else.
 *
 * The stand-in card is not here. It is typed where a card is handed over — the
 * enrolment — or corrected on the card itself in Card Information. Switching a
 * member to canvassing with no number on file is refused by the API, and the
 * refusal is shown under the field.
 *
 * The list is legacy's Member-tier one: Walk-In, Canvassing and Online. Online
 * was left out here once, on the grounds that it is settled at enrolment — but
 * a member enrolled online whose channel was keyed in wrongly then had no way
 * back to it.
 *
 * The kiosks are on the list too. They once took a second codename on top of
 * the right to edit the field at all, on the argument that a machine enrolment
 * is a correction of an import rather than a day's work at a club — one right
 * governs the field now, and whoever may correct the channel may correct it to
 * any of them. "Benefits" is still nobody's: it is the other three tiers'
 * channel and this sheet is only ever a member's.
 */

import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useId, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";
import { Field, Form } from "@/components/ui/field";
import { OptionSelect } from "@/components/ui/option-select";
import { channelsFor, type Member } from "@/pages/MembersPage";
import { useRank } from "@/utils/permissions";
import { api, errorMessage, ifMatch } from "@/services/api";
import { useTranslation } from "@/store/locale";

/**
 * The channels this person may set on a member, in the order they are listed.
 *
 * Three pickers on the detail page ask the same question — this sheet, the
 * amendments that land an account on Member tier, and the channel a card is
 * moved under — and all of them read it from here, so the rule lives in one
 * place. `channelsFor` is that rule: never Benefits, and the kiosks only for a
 * Manager and above, which leaves an Associate or a Supervisor the three a club
 * takes over the counter. `current` is the account's own channel, which stays
 * on the list for whoever opens it — see `channelsFor`.
 *
 * Member-tier only, which is every caller: the other three tiers are enrolled
 * as a benefit, that is not a choice, and those pickers are fixed and disabled
 * where they stand.
 */
export function useMemberChannels(current?: string | null) {
  const t = useTranslation();
  return channelsFor(t, useRank(), current);
}

export function AcquisitionDialog({
  member,
  onClose,
  onSaved,
}: {
  member: Member;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslation();
  // The member's own channel goes on the list whatever the rank: a kiosk
  // enrolment is not a Manager's to correct and everybody else's to overwrite.
  const choices = useMemberChannels(member.acquisition_type);
  const [value, setValue] = useState(member.acquisition_type ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (acquisition_type: string) =>
      api.patch(
        `/members/${member.id}/acquisition-type`,
        { acquisition_type },
        ifMatch(member),
      ),
    onSuccess: () => {
      toast.success(t("md.acquisitionUpdated"));
      onSaved();
    },
    onError: (err) => setError(errorMessage(err, t("md.acquisitionFailed"))),
  });

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    setError(null);
    save.mutate(value);
  };

  // Its own id, and pressed directly — see `CardInfoDialog`.
  const formId = useId();
  return (
    <Modal
      open
      onClose={onClose}
      title={t("rp.editSingular").replace("{singular}", t("mem.acquisitionType"))}
      className="sm:max-w-[35rem]"
      footer={
        <>
          <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => submit()}
            className="flex-1"
            loading={save.isPending}
            loadingLabel={t("common.saving")}
            disabled={!value}
          >
            {t("common.submit")}
          </Button>
        </>
      }
    >
      <Form id={formId} className="grid gap-4 pb-2 sm:grid-cols-2" onSubmit={submit}>
        <Field label={t("mem.acquisitionType")} htmlFor="acquisition-type">
          <OptionSelect
            id="acquisition-type"
            value={value}
            onChange={setValue}
            choices={choices}
            placeholder={t("new.selectAcquisition")}
            required
          />
        </Field>
        {error ? (
          <p role="alert" className="text-danger text-callout sm:col-span-2">
            {error}
          </p>
        ) : null}
      </Form>
    </Modal>
  );
}
